/**
 * Employee form defaults — shared by `new.tsx` and `$employeeId.edit.tsx`.
 *
 * Same pattern as `features/vendors/lib/form-defaults.ts`: RHF controlled
 * inputs default to `''`; the submit handler strips empty optionals before
 * posting so the wire stays lean (no `pan: ''` rows the contract rejects).
 *
 * Money is entered in rupees in the form and converted to integer-paise
 * strings at the wire (the contract uses `moneyPaise`). PII (PAN / Aadhaar /
 * bank account) is plaintext on the wire — the domain wraps it.
 */
import type { employees } from "@flint/api-contract";
import type { z } from "zod";
import { rupeesToPaiseString } from "../../../lib/format.ts";

export type EmployeePayMode = z.infer<typeof employees.employeePayMode>;

/** Form-side value shape. Strings are always non-undefined (RHF wants
 *  controlled inputs); money fields are rupee strings; the submit handler
 *  coerces to the wire shape per field before posting. */
export interface EmployeeFormValues {
  name: string;
  designation: string;
  employeeCode: string;
  pan: string;
  aadhaar: string;
  dateOfJoining: string;
  /** Rupees (e.g. "120000"); converted to paise at submit. */
  monthlyGross: string;
  defaultPf: string;
  defaultPt: string;
  payMode: EmployeePayMode;
  bankName: string;
  bankAccount: string;
  bankIfsc: string;
  /** A `memberships.id`, or "" for "no login". */
  membershipId: string;
}

export const EMPTY_FORM: EmployeeFormValues = {
  name: "",
  designation: "",
  employeeCode: "",
  pan: "",
  aadhaar: "",
  dateOfJoining: "",
  monthlyGross: "",
  defaultPf: "0",
  defaultPt: "0",
  payMode: "neft",
  bankName: "",
  bankAccount: "",
  bankIfsc: "",
  membershipId: "",
};

type EmployeeRow = z.infer<typeof employees.employeeRow>;

/** Convert a paise string to a plain rupee string for the form inputs. */
function paiseToRupeeInput(paise: string): string {
  if (!paise) return "0";
  return (BigInt(paise) / 100n).toString();
}

/**
 * Hydrate the form from a fetched employee. PII comes from a `reveal: true`
 * fetch so Aadhaar/bank prefill with full values (PAN is always full).
 */
export function formFromEmployee(e: EmployeeRow): EmployeeFormValues {
  return {
    name: e.name,
    designation: e.designation ?? "",
    employeeCode: e.employeeCode ?? "",
    pan: e.pan ?? "",
    aadhaar: e.aadhaar ?? "",
    dateOfJoining: e.dateOfJoining,
    monthlyGross: paiseToRupeeInput(e.monthlyGrossPaise),
    defaultPf: paiseToRupeeInput(e.defaultPfPaise),
    defaultPt: paiseToRupeeInput(e.defaultPtPaise),
    payMode: e.payMode,
    bankName: e.bankName ?? "",
    bankAccount: e.bankAccount ?? "",
    bankIfsc: e.bankIfsc ?? "",
    membershipId: e.membershipId ?? "",
  };
}

/** Build the create payload — drop empty optionals; money → paise. */
export function valuesToCreatePayload(
  v: EmployeeFormValues,
): z.infer<typeof employees.createInput> {
  const payload: z.infer<typeof employees.createInput> = {
    name: v.name.trim(),
    dateOfJoining: v.dateOfJoining,
    monthlyGrossPaise: rupeesToPaiseString(v.monthlyGross) ?? "0",
  };
  if (v.designation.trim()) payload.designation = v.designation.trim();
  if (v.employeeCode.trim()) payload.employeeCode = v.employeeCode.trim();
  if (v.pan.trim()) payload.pan = v.pan.trim().toUpperCase();
  if (v.aadhaar.trim()) payload.aadhaar = v.aadhaar.replace(/\s/g, "");
  const pf = rupeesToPaiseString(v.defaultPf);
  if (pf && pf !== "0") payload.defaultPfPaise = pf;
  const pt = rupeesToPaiseString(v.defaultPt);
  if (pt && pt !== "0") payload.defaultPtPaise = pt;
  payload.payMode = v.payMode;
  if (v.bankName.trim()) payload.bankName = v.bankName.trim();
  if (v.bankAccount.trim()) payload.bankAccount = v.bankAccount.replace(/\s/g, "");
  if (v.bankIfsc.trim()) payload.bankIfsc = v.bankIfsc.trim().toUpperCase();
  if (v.membershipId) payload.membershipId = v.membershipId;
  return payload;
}

/** Build the update patch — diff against the baseline; money → paise. */
export function valuesToUpdatePayload(
  v: EmployeeFormValues,
  base: EmployeeFormValues,
  id: string,
): z.infer<typeof employees.updateInput> {
  const patch: z.infer<typeof employees.updateInput> = { id };

  if (v.name.trim() !== base.name.trim()) patch.name = v.name.trim();

  // Plain strings — diff trimmed values; '' is a legal "clear" value.
  const stringFields = ["designation", "employeeCode", "bankName"] as const;
  for (const k of stringFields) {
    const next = v[k].trim();
    if (next !== base[k].trim()) (patch as Record<string, unknown>)[k] = next;
  }
  // IFSC + PII normalise (uppercase / strip spaces) before diffing.
  if (v.bankIfsc.trim().toUpperCase() !== base.bankIfsc.trim().toUpperCase()) {
    patch.bankIfsc = v.bankIfsc.trim().toUpperCase();
  }
  if (v.pan.trim().toUpperCase() !== base.pan.trim().toUpperCase()) {
    patch.pan = v.pan.trim().toUpperCase();
  }
  const nextAadhaar = v.aadhaar.replace(/\s/g, "");
  const baseAadhaar = base.aadhaar.replace(/\s/g, "");
  if (nextAadhaar !== baseAadhaar) patch.aadhaar = nextAadhaar;
  const nextAccount = v.bankAccount.replace(/\s/g, "");
  const baseAccount = base.bankAccount.replace(/\s/g, "");
  if (nextAccount !== baseAccount) patch.bankAccount = nextAccount;

  if (v.dateOfJoining !== base.dateOfJoining) patch.dateOfJoining = v.dateOfJoining;
  if (v.payMode !== base.payMode) patch.payMode = v.payMode;
  if (v.membershipId !== base.membershipId) patch.membershipId = v.membershipId || undefined;

  // Money — compare paise so "120000" vs "120000.00" don't false-diff.
  const moneyFields = [
    { form: "monthlyGross", wire: "monthlyGrossPaise" },
    { form: "defaultPf", wire: "defaultPfPaise" },
    { form: "defaultPt", wire: "defaultPtPaise" },
  ] as const;
  for (const { form, wire } of moneyFields) {
    const next = rupeesToPaiseString(v[form]) ?? "0";
    const prev = rupeesToPaiseString(base[form]) ?? "0";
    if (next !== prev) (patch as Record<string, unknown>)[wire] = next;
  }

  return patch;
}
